import { useState } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { useMutation, usePaginatedQuery } from "convex/react";
import { api } from "@constructionautomation/backend";
import type { Id } from "@constructionautomation/backend/convex/_generated/dataModel";
import { Archive, CheckCircle2, ChevronDown, Eye, Loader2, MoreHorizontal, RotateCcw } from "lucide-react";
import { toast } from "sonner";

import { PageHeader } from "@/components/layout";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { useInfiniteScroll } from "@/lib/use-infinite-scroll";
import { cn } from "@/lib/utils";

export const Route = createFileRoute("/admin/feedback")({
  component: AdminFeedbackPage,
});

const PAGE_SIZE = 25;
const statusOptions = ["all", "new", "reviewed", "archived"] as const;
const feedbackStatuses = ["new", "reviewed", "archived"] as const;

type StatusFilter = (typeof statusOptions)[number];
type FeedbackStatus = Exclude<StatusFilter, "all">;
type FeedbackSubmissionId = Id<"feedbackSubmissions">;

function formatDate(timestamp: number) {
  return new Intl.DateTimeFormat(undefined, {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
  }).format(new Date(timestamp));
}

function statusBadgeClass(status: FeedbackStatus) {
  if (status === "new") return "border-blue-500/60 bg-blue-500/15 text-blue-700 dark:text-blue-300";
  if (status === "reviewed") return "border-green-500/60 bg-green-500/15 text-green-700 dark:text-green-300";
  return "border-muted-foreground/40 bg-muted text-muted-foreground";
}

function formatStatusLabel(status: FeedbackStatus) {
  return status.charAt(0).toUpperCase() + status.slice(1);
}

function AdminFeedbackPage() {
  const [statusFilter, setStatusFilter] = useState<StatusFilter>("new");
  const [updatingId, setUpdatingId] = useState<string | null>(null);
  const [selectedFeedbackId, setSelectedFeedbackId] = useState<string | null>(null);
  const [technicalDetailsOpen, setTechnicalDetailsOpen] = useState(false);
  const updateStatus = useMutation(api.feedback.updateStatus);
  const feedbackQuery = usePaginatedQuery(
    api.feedback.listForAdmin,
    statusFilter === "all" ? {} : { status: statusFilter },
    { initialNumItems: PAGE_SIZE }
  );
  const loadMoreRef = useInfiniteScroll(
    feedbackQuery.status === "CanLoadMore",
    feedbackQuery.loadMore,
    PAGE_SIZE
  );

  const handleUpdateStatus = async (feedbackId: string, status: FeedbackStatus) => {
    setUpdatingId(feedbackId);
    try {
      await updateStatus({
        feedbackId: feedbackId as FeedbackSubmissionId,
        status,
      });
      toast.success(`Feedback marked ${status}`);
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "Unable to update feedback");
    } finally {
      setUpdatingId(null);
    }
  };

  const isLoadingFirstPage =
    feedbackQuery.status === "LoadingFirstPage" && feedbackQuery.results.length === 0;
  const selectedFeedback = feedbackQuery.results.find((feedback) => feedback._id === selectedFeedbackId);

  return (
    <div className="space-y-6">
      <PageHeader
        title="Feedback"
        description="Review product feedback submitted from the web app."
        actions={
          <Select value={statusFilter} onValueChange={(value) => setStatusFilter(value as StatusFilter)}>
            <SelectTrigger className="w-40 border-2">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
                  {statusOptions.map((status) => (
                <SelectItem key={status} value={status}>
                  {status === "all" ? "All" : formatStatusLabel(status)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        }
      />

      <Card className="py-4">
        <CardHeader className="pb-2">
          <CardTitle className="text-base">Submitted Feedback</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="rounded-lg border-2 border-border">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-[220px] text-xs font-bold uppercase tracking-wider">User</TableHead>
                  <TableHead className="text-xs font-bold uppercase tracking-wider">Feedback</TableHead>
                  <TableHead className="w-[170px] text-xs font-bold uppercase tracking-wider">Status</TableHead>
                  <TableHead className="w-[180px] text-xs font-bold uppercase tracking-wider">Submitted</TableHead>
                  <TableHead className="w-[80px] text-right text-xs font-bold uppercase tracking-wider">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {isLoadingFirstPage ? (
                  <TableRow>
                    <TableCell colSpan={5} className="py-12 text-center text-muted-foreground">
                      <Loader2 className="mx-auto size-6 animate-spin" />
                    </TableCell>
                  </TableRow>
                ) : feedbackQuery.results.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={5} className="py-12 text-center text-muted-foreground">
                      No feedback submissions found.
                    </TableCell>
                  </TableRow>
                ) : (
                  feedbackQuery.results.map((feedback) => (
                    <TableRow key={feedback._id} className="align-top">
                      <TableCell>
                        <div className="space-y-1">
                          <p className="font-semibold text-foreground">{feedback.email}</p>
                          {feedback.userName ? (
                            <p className="text-xs text-muted-foreground">{feedback.userName}</p>
                          ) : null}
                          {feedback.organizationName ? (
                            <p className="text-xs text-muted-foreground">{feedback.organizationName}</p>
                          ) : null}
                        </div>
                      </TableCell>
                      <TableCell>
                        <button
                          type="button"
                          className="line-clamp-2 max-w-3xl text-left text-sm leading-6 text-foreground hover:underline"
                          onClick={() => setSelectedFeedbackId(feedback._id)}
                        >
                          {feedback.message}
                        </button>
                      </TableCell>
                      <TableCell>
                        <Select
                          value={feedback.status}
                          disabled={updatingId === feedback._id}
                          onValueChange={(value) => void handleUpdateStatus(feedback._id, value as FeedbackStatus)}
                        >
                          <SelectTrigger
                            className={cn(
                              "h-9 w-36 border-2 text-xs font-bold uppercase tracking-wider",
                              statusBadgeClass(feedback.status)
                            )}
                          >
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {feedbackStatuses.map((status) => (
                              <SelectItem key={status} value={status}>
                                {formatStatusLabel(status)}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </TableCell>
                      <TableCell className="text-sm text-muted-foreground">
                        {formatDate(feedback.createdAt)}
                      </TableCell>
                      <TableCell>
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="icon-sm" aria-label="Feedback actions">
                              <MoreHorizontal className="size-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end" className="w-44 border-2">
                            <DropdownMenuItem onSelect={() => setSelectedFeedbackId(feedback._id)}>
                              <Eye className="size-4" />
                              View Details
                            </DropdownMenuItem>
                            {feedback.status !== "new" ? (
                              <DropdownMenuItem onSelect={() => void handleUpdateStatus(feedback._id, "new")}>
                                <RotateCcw className="size-4" />
                                Reopen
                              </DropdownMenuItem>
                            ) : null}
                            {feedback.status !== "reviewed" ? (
                              <DropdownMenuItem onSelect={() => void handleUpdateStatus(feedback._id, "reviewed")}>
                                <CheckCircle2 className="size-4" />
                                Mark Reviewed
                              </DropdownMenuItem>
                            ) : null}
                            {feedback.status !== "archived" ? (
                              <DropdownMenuItem onSelect={() => void handleUpdateStatus(feedback._id, "archived")}>
                                <Archive className="size-4" />
                                Archive
                              </DropdownMenuItem>
                            ) : null}
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>

          <div ref={loadMoreRef} className="flex min-h-12 items-center justify-center pt-4">
            {feedbackQuery.status === "LoadingMore" ? (
              <Loader2 className="size-5 animate-spin text-muted-foreground" />
            ) : feedbackQuery.status === "CanLoadMore" ? (
              <Button variant="outline" onClick={() => feedbackQuery.loadMore(PAGE_SIZE)}>
                Load More
              </Button>
            ) : null}
          </div>
        </CardContent>
      </Card>

      <Dialog open={Boolean(selectedFeedback)} onOpenChange={(open) => !open && setSelectedFeedbackId(null)}>
        {selectedFeedback ? (
          <DialogContent className="border-2 sm:max-w-2xl">
            <DialogHeader>
              <DialogTitle>Feedback Details</DialogTitle>
              <DialogDescription>
                Submitted by {selectedFeedback.email} on {formatDate(selectedFeedback.createdAt)}.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-5">
              <div className="rounded-lg border-2 border-border bg-muted/30 p-4">
                <p className="whitespace-pre-wrap text-sm leading-6">{selectedFeedback.message}</p>
              </div>
              <div className="grid gap-3 text-sm sm:grid-cols-2">
                <DetailItem label="Email" value={selectedFeedback.email} />
                <DetailItem label="Name" value={selectedFeedback.userName} />
                <DetailItem label="Organization" value={selectedFeedback.organizationName} />
                <DetailItem label="Status" value={formatStatusLabel(selectedFeedback.status)} />
              </div>
              <Collapsible open={technicalDetailsOpen} onOpenChange={setTechnicalDetailsOpen}>
                <CollapsibleTrigger asChild>
                  <Button variant="outline" className="w-full justify-between">
                    Technical Details
                    <ChevronDown
                      className={cn(
                        "size-4 transition-transform",
                        technicalDetailsOpen && "rotate-180"
                      )}
                    />
                  </Button>
                </CollapsibleTrigger>
                <CollapsibleContent className="pt-4">
                  <div className="grid gap-3 rounded-lg border-2 border-border bg-muted/20 p-4 text-sm sm:grid-cols-2">
                    <DetailItem label="Path" value={selectedFeedback.pathname} />
                    <DetailItem label="Organization ID" value={selectedFeedback.organizationId} />
                    <DetailItem label="User ID" value={selectedFeedback.userId} />
                    <DetailItem label="WorkOS User ID" value={selectedFeedback.workosUserId} />
                    <DetailItem label="WorkOS Organization ID" value={selectedFeedback.workosOrganizationId} />
                    <DetailItem className="sm:col-span-2" label="User Agent" value={selectedFeedback.userAgent} />
                  </div>
                </CollapsibleContent>
              </Collapsible>
            </div>
          </DialogContent>
        ) : null}
      </Dialog>
    </div>
  );
}

function DetailItem({
  label,
  value,
  className,
}: {
  label: string;
  value: string | null | undefined;
  className?: string;
}) {
  return (
    <div className={cn("grid gap-1", className)}>
      <span className="text-xs font-bold uppercase tracking-wider text-muted-foreground">{label}</span>
      <span className="break-all text-foreground">{value || "Not captured"}</span>
    </div>
  );
}
